import { useId, useState } from 'react';
import { useLocale } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface SeriespillHistoryModalProps {
  /** Classes whose `seriesHistory` archives are browsed. */
  classes?: any[];
  /** Closes the modal. */
  onClose: () => void;
  /** Optional initial selection: { classId, idx } into a class's seriesHistory. */
  initialSel?: { classId: string; idx: number } | null;
}

/** Modal that browses archived seriespill seasons per class — picks a season, then shows its final tables and played matches per division. Extracted verbatim from the monolith (legacy/App.tsx). */
export function SeriespillHistoryModal({
  classes,
  onClose,
  initialSel = null,
}: SeriespillHistoryModalProps) {
  const { t, formatDate } = useLocale();
  // The series gender palette is the AA shade of each hue (#1295, as #1293
  // did for KM): white on it was 3.68 / 3.53 / 4.23:1, now 5.69 / 5.89 / 5.65.
  const C = { M: '#1961d3', F: '#c01074', X: '#7845df' };
  const L = {
    M: t('seriespill.common.genderMen'),
    F: t('seriespill.common.genderWomen'),
    X: t('seriespill.common.genderMix'),
  };
  const [sel, setSel] = useState(initialSel || null); // { classId, idx } | null
  const [histTab, setHistTab] = useState('table'); // 'table' | 'matches'
  const titleId = safeId('serie-history-title', useId());
  // DEVIATION (lint): `setOpenMatches` is declared-but-never-called in the
  // monolith (the per-division match expander state was never wired up);
  // `_`-prefixed to satisfy no-unused-vars without changing behaviour.
  const [openMatches, _setOpenMatches] = useState({});
  const fmt = (ms) =>
    ms ? formatDate(new Date(ms), { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const withHistory = (classes || []).filter((c) => (c.seriesHistory || []).length > 0);
  const selClass = sel ? withHistory.find((c) => c.id === sel.classId) : null;
  const selSeason = selClass ? (selClass.seriesHistory || [])[sel.idx] : null;
  const stripNum = (name) => {
    const s = (name || '').replace(/^\s*\d+\.?\s*/, '').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : name || '';
  };
  const rank = (arr) =>
    [...(arr || [])].sort(
      (a, b) =>
        b.points - a.points ||
        b.setsFor - b.setsAgainst - (a.setsFor - a.setsAgainst) ||
        b.wins - a.wins,
    );
  const pairName = (season, pid) => {
    const p = (season.pairs || []).find((x) => x.id === pid);
    return p ? `${p.p1Name} / ${p.p2Name}` : '—';
  };

  return (
    // A `Dialog` (#900): named by its title (which follows the chosen season),
    // Tab kept inside, Escape closes as the backdrop does, focus given back.
    <Dialog
      labelledBy={titleId}
      onClose={onClose}
      showClose={false}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-3 bg-ink-1/40 backdrop-blur-sm"
      className="bg-paper rounded-2xl shadow-xl w-full max-w-2xl"
      style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
    >
      <div className="p-4 border-b border-line flex items-center justify-between gap-2 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {sel && (
            <button
              type="button"
              onClick={() => setSel(null)}
              aria-label={t('seriespill.history.back')}
              className="text-ink-3 hover:text-ink-1 text-sm flex-shrink-0"
            >
              ←
            </button>
          )}
          <h3 id={titleId} className="font-display text-lg font-semibold text-ink-1 truncate">
            {sel
              ? t('seriespill.history.title', {
                  name: selClass?.name || L[selClass?.gender] || '',
                  season: String(selSeason?.season),
                })
              : t('seriespill.history.allTitle')}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('ui.dialog.close')}
          className="text-ink-3 hover:text-ink-1 text-lg leading-none flex-shrink-0"
        >
          ✕
        </button>
      </div>
      <div className="p-4 overflow-y-auto">
        {!sel ? (
          withHistory.length === 0 ? (
            <p className="text-sm text-ink-3 italic text-center py-6">
              {t('seriespill.history.empty')}
            </p>
          ) : (
            withHistory.map((c) => (
              <div key={c.id} className="mb-4 last:mb-0">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: C[c.gender] || '#9ca3af',
                      display: 'inline-block',
                    }}
                  />
                  <span className="font-display font-bold text-ink-1 text-[15px]">
                    {c.name || L[c.gender] || ''}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {(c.seriesHistory || []).map((s, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setSel({ classId: c.id, idx });
                        setHistTab('table');
                      }}
                      className="w-full flex items-center justify-between gap-2 rounded-lg border border-line px-3 py-2 hover:bg-bg-soft transition-colors text-left"
                    >
                      <span className="font-semibold text-ink-1 text-sm">
                        {t('seriespill.history.season', { season: String(s.season) })}
                      </span>
                      <span className="font-mono text-[11px] text-ink-3">
                        {t(
                          (s.divisions || []).length === 1
                            ? 'seriespill.history.divisionCountOne'
                            : 'seriespill.history.divisionCountMany',
                          { count: String((s.divisions || []).length) },
                        )}
                        {' · '}
                        {fmt(s.endedAt)} →
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2 sticky top-0 z-10 bg-paper pb-1">
              <button
                onClick={() => setHistTab('table')}
                aria-pressed={histTab === 'table'}
                className={`flex-1 text-xs font-mono uppercase tracking-wider px-3 py-2 rounded-lg border-2 ${histTab === 'table' ? 'border-lime-deep bg-lime-bg/30 text-lime-deep font-semibold' : 'border-line text-ink-3'}`}
              >
                {t('seriespill.history.tabTable')}
              </button>
              <button
                onClick={() => setHistTab('matches')}
                aria-pressed={histTab === 'matches'}
                className={`flex-1 text-xs font-mono uppercase tracking-wider px-3 py-2 rounded-lg border-2 ${histTab === 'matches' ? 'border-lime-deep bg-lime-bg/30 text-lime-deep font-semibold' : 'border-line text-ink-3'}`}
              >
                {t('seriespill.history.tabMatches')}
              </button>
            </div>
            {[...(selSeason?.divisions || [])]
              .sort((a, b) => (a.level || 0) - (b.level || 0))
              .map((div) => {
                const maxLevel = Math.max(
                  1,
                  ...(selSeason?.divisions || []).map((d) => d.level || 1),
                );
                const standings = rank(selSeason.standings?.[div.id]);
                const promoteCount = div.level > 1 ? (div.promote ?? 2) : 0;
                const relegateCount = div.level < maxLevel ? (div.relegate ?? 2) : 0;
                const relStart = standings.length - relegateCount;
                const matches = (selSeason.matches || []).filter((m) => m.divisionId === div.id);
                // DEVIATION (lint): `mOpen` is computed-but-never-read in the
                // monolith (dead alongside the unused setOpenMatches);
                // `_`-prefixed to satisfy no-unused-vars. Otherwise byte-identical.
                const _mOpen = !!openMatches[div.id];
                const byRound = {};
                matches.forEach((m) => {
                  (byRound[m.round] = byRound[m.round] || []).push(m);
                });
                const rounds = Object.keys(byRound)
                  .map(Number)
                  .sort((a, b) => a - b);
                return (
                  <div key={div.id} className="card-elev p-3">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span
                        className="grid place-items-center font-display font-bold text-white"
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 8,
                          background: C[selClass.gender] || '#9ca3af',
                          fontSize: 13,
                          lineHeight: 1,
                        }}
                      >
                        {div.level ?? ''}
                      </span>
                      <span className="font-display font-bold text-ink-1 text-[16px]">
                        {stripNum(div.name)}
                      </span>
                    </div>
                    {histTab === 'table' &&
                      (standings.length === 0 ? (
                        <p className="text-[12px] text-ink-3 italic text-center py-2">
                          {t('seriespill.history.noData')}
                        </p>
                      ) : (
                        <div className="overflow-x-auto rounded-lg border border-line">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="text-[9px] uppercase tracking-wider text-ink-3 bg-bg-soft">
                                <th className="p-0" style={{ width: 4 }}></th>
                                <th className="text-center py-1.5 px-2" style={{ width: 30 }}>
                                  #
                                </th>
                                <th className="text-left py-1.5 pr-2">
                                  {t('seriespill.table.pair')}
                                </th>
                                <th className="text-center py-1.5 px-1.5">
                                  {t('seriespill.table.played')}
                                </th>
                                <th className="text-center py-1.5 px-1.5">
                                  {t('seriespill.table.wins')}
                                </th>
                                <th className="text-center py-1.5 px-1.5">
                                  {t('seriespill.table.draws')}
                                </th>
                                <th className="text-center py-1.5 px-1.5">
                                  {t('seriespill.table.losses')}
                                </th>
                                <th className="text-center py-1.5 px-1.5">+/−</th>
                                <th className="text-center py-1.5 px-2 font-bold text-ink-2">
                                  {t('seriespill.table.points')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {standings.map((s, i) => {
                                const diff = (s.setsFor || 0) - (s.setsAgainst || 0);
                                const inPromote = i < promoteCount;
                                const inRelegate = i >= relStart;
                                const railColor = inPromote
                                  ? '#16a34a'
                                  : inRelegate
                                    ? '#dc2626'
                                    : 'transparent';
                                const rowBg = inPromote
                                  ? 'rgba(22,163,74,0.06)'
                                  : inRelegate
                                    ? 'rgba(220,38,38,0.06)'
                                    : i % 2 === 1
                                      ? 'rgba(0,0,0,0.018)'
                                      : 'transparent';
                                return (
                                  <tr
                                    key={s.pair?.id || i}
                                    className="border-t border-line/60"
                                    style={{ background: rowBg }}
                                  >
                                    <td
                                      className="p-0"
                                      style={{ width: 4, background: railColor }}
                                    ></td>
                                    <td className="text-center py-1.5 px-2 font-mono text-ink-3">
                                      {i + 1}
                                    </td>
                                    <td className="text-left py-1.5 pr-2 truncate">
                                      {s.pair ? `${s.pair.p1Name} / ${s.pair.p2Name}` : '—'}
                                    </td>
                                    <td className="text-center py-1.5 px-1.5 font-mono text-ink-2">
                                      {s.played}
                                    </td>
                                    <td className="text-center py-1.5 px-1.5 font-mono text-lime-deep">
                                      {s.wins}
                                    </td>
                                    <td className="text-center py-1.5 px-1.5 font-mono text-ink-3">
                                      {s.draws || 0}
                                    </td>
                                    <td className="text-center py-1.5 px-1.5 font-mono text-rose">
                                      {s.losses}
                                    </td>
                                    <td className="text-center py-1.5 px-1.5 font-mono text-[12px] text-ink-3">
                                      {diff > 0 ? '+' : ''}
                                      {diff}
                                    </td>
                                    <td className="text-center py-1.5 px-2 font-bold">
                                      {s.points}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    {histTab === 'matches' &&
                      (matches.length > 0 ? (
                        <div className="space-y-2">
                          {rounds.map((r) => (
                            <div key={r}>
                              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
                                {t('seriespill.history.round', { round: String(r) })}
                              </div>
                              <div className="space-y-1">
                                {byRound[r].map((m, mi) => {
                                  const t1w = m.score && m.score.t1 > m.score.t2;
                                  const t2w = m.score && m.score.t2 > m.score.t1;
                                  return (
                                    <div
                                      key={mi}
                                      className="flex items-center justify-between gap-2 text-[12px] py-1 px-2 rounded bg-bg-soft/30"
                                    >
                                      <span
                                        className={`truncate flex-1 text-right ${t1w ? 'font-semibold text-ink-1' : 'text-ink-3'}`}
                                      >
                                        {pairName(selSeason, m.team1?.pairId)}
                                      </span>
                                      <span
                                        className="font-mono font-bold flex-shrink-0 px-1.5"
                                        style={{
                                          color: m.score
                                            ? t1w || t2w
                                              ? 'var(--lime-text)'
                                              : 'var(--ink-3)'
                                            : 'var(--ink-3)',
                                        }}
                                      >
                                        {m.score
                                          ? m.score.doubleWalkover
                                            ? 'WO'
                                            : m.score.draw
                                              ? t('seriespill.history.drawShort')
                                              : `${m.score.t1}–${m.score.t2}`
                                          : '–'}
                                      </span>
                                      <span
                                        className={`truncate flex-1 text-left ${t2w ? 'font-semibold text-ink-1' : 'text-ink-3'}`}
                                      >
                                        {pairName(selSeason, m.team2?.pairId)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[12px] text-ink-3 italic text-center py-2">
                          {t('seriespill.history.noMatches')}
                        </p>
                      ))}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </Dialog>
  );
}
